import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "/vendor/react";
import { Box } from "/vendor/@material-ui/core";

import { SearchBox, FilterList } from "/lib/component/base";
import { useFilterParams, useSearchParams } from "/lib/component/util";
import {
  buildFilterParams,
  parseFilterParams,
  FilterParamMap,
} from "/lib/util/filterParams";
import { parseArrayParam } from "/lib/util/params";

const SEARCH_FILTER_KEY = "name";
const DEBOUNCE_MS = 300;

interface Props {
  "aria-label"?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

/*
 * NameSearchBox - live search over resource names.
 *
 * Typing updates the input immediately; after a short debounce the
 * `name:<query>` GraphQL filter is written to the URL (and offset reset),
 * so the backend scans the whole resource pool instead of only the current
 * page. Remaining filters are rendered as chips below the input.
 */
const NameSearchBox = ({
  "aria-label": ariaLabel,
  placeholder,
  autoFocus = true,
}: Props) => {
  const [filters, setFilters] = useFilterParams();
  const [, setParams] = useSearchParams();

  const urlValue = filters[SEARCH_FILTER_KEY] || "";
  const [value, setValue] = useState(urlValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the input in sync when filters change externally (Reset, back, etc.).
  useEffect(() => {
    setValue(urlValue);
  }, [urlValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const applySearch = useCallback(
    (raw: string) => {
      const next = raw.trim();

      setParams((params) => {
        const current = parseFilterParams(parseArrayParam(params.filters));
        if ((current[SEARCH_FILTER_KEY] || "") === next) {
          return params;
        }

        return {
          ...params,
          offset: undefined,
          filters: buildFilterParams({
            ...current,
            [SEARCH_FILTER_KEY]: next || undefined,
          }),
        };
      });
    },
    [setParams],
  );

  const onChange = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const next = ev.currentTarget.value;
      setValue(next);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => applySearch(next), DEBOUNCE_MS);
    },
    [applySearch],
  );

  const { [SEARCH_FILTER_KEY]: _name, ...chipFilters } = filters;

  const onChipChange = useCallback(
    (action: (prev: FilterParamMap) => FilterParamMap) => {
      setFilters((prev) => {
        const next = action(prev);
        if (prev[SEARCH_FILTER_KEY]) {
          return { ...next, [SEARCH_FILTER_KEY]: prev[SEARCH_FILTER_KEY] };
        }
        return next;
      });
    },
    [setFilters],
  );

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <SearchBox
        aria-label={ariaLabel}
        variant="search"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
      />
      {Object.keys(chipFilters).length > 0 && (
        <Box pl={1} pr={1}>
          <FilterList filters={chipFilters} onChange={onChipChange} />
        </Box>
      )}
    </Box>
  );
};

export default NameSearchBox;
